import { AxiosResponse } from 'axios';
import getConfig from 'next/config';
import Head from 'next/head';
import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
    CmsPage,
    DayPassAvailability,
    GymProduct,
    OptimizelyFeatureFlags,
    PlaceRating,
    Product,
} from '@tgg/common-types';
import { axiosInstance } from '@tgg/micro-services/axios';
import { Metadata } from '@tgg/micro-services/cms-client';
import { getPlaceRating, getTrustpilotData, GymDto } from '@tgg/services';
import { CmsContent, getLoggerPaymentData, useFlagsContext } from '@tgg/ui';
import {
    GsspWithLoggerParameters,
    OptimizelyEvents,
    formatProductsForUI,
    getFromPriceForProducts,
    gsspWithLogger,
} from '@tgg/util';

import { applicationIdentifier } from 'apps/commerce/core/constants';
import {
    getProductsAvailableAddons,
    handleProductSelection,
} from 'apps/commerce/core/products';
import { amplienceUrl } from 'libs/ui/src/lib/components/ImageStandalone/ImageStandalone.utils';
import { Tile } from 'libs/ui/src/lib/components/MediaGallery/MediaGallery.types';

import {
    getShoppingBasketCookie,
    updateShoppingBasketCookie,
} from '../../core/cookies';
import { LocalConfig, PublicRuntimeConfig } from '../../next.config.types';

import { getLocalBusinessData } from './GymPage.helpers';
import { GymPageProperties, JoinOptionsResponse } from './GymPage.types';

const {
    publicRuntimeConfig: { GOOGLE_RATING_WIDGET_ENABLED },
}: {
    publicRuntimeConfig: PublicRuntimeConfig;
} = getConfig();

const MINIMUM_RATING_TO_SHOW = 4;

export function GymPage({
    branchId,
    gymName,
    products,
    trustpilotData,
    address,
    openingHours,
    latitude,
    longitude,
    content,
    gym,
    productsAvailableAddons,
    offPeakTimes,
    isKickerOffersRibbonEnabled,
    gymRating,
    offSale,
    branchStatus,
    promoSnackBarOptions,
    localBusinessData,
}: GymPageProperties) {
    const { trackFlagsEvent } = useFlagsContext();

    const onSelectProduct = async (
        product: Product,
        dayPassStartDate?: DayPassAvailability,
    ) => {
        resetCookieForJoinJourney();

        await handleProductSelection(
            product,
            branchId,
            gymName,
            branchStatus,
            dayPassStartDate,
            trackFlagsEvent,
        );

        // 1) PinoProducts to PinoProductOptions and handleJoinNow
        // // onSelect(optionSelected);
        // sendAnalyticsDataEvent({
        // //     eventName: DataEventNames.JOIN_NOW,
        // //     eventData: optionSelected,
        // //     eventGymName: gymName,
        // // });
        // const generatedCTAHref = generateCTAHref(
        // //     productsAvailableAddons,
        // //     optionSelected,
        // //     ctaHref,
        // // );
        // await router.push(generatedCTAHref);

        // 2) MonthlyPanels to two buttonbase buttons
        // // onSelect(optionSelected);

        // 3) Student Panel to PinoStudentProductOptions to handleJoinNow
        // // onSelect(optionSelected);
        // sendAnalyticsDataEvent({
        // //     eventName: DataEventNames.JOIN_NOW,
        // //     eventData: optionSelected,
        // //     eventGymName: gymName,
        // // });
        // // const generatedCTAHref = generateCTAHref(
        // //     productsAvailableAddons,
        // //     optionSelected,
        // //     ctaHref,
        // // );
        // // await router.push(generatedCTAHref);

        // 4) DayPassesPanel to handleDayPassProductSelection (custom hook) to PinoDayPassProductOptions to handleClick
        // // sendAnalyticsDataEvent({
        // //     eventName: DataEventNames.JOIN_NOW,
        // //     eventData: optionSelected,
        // //     eventGymName: gymName,
        // // });
        // // onSelect(optionSelected);

        // 5) DayPassSelection to StickyNavigationButton to ButtonBase
    };

    useEffect(() => {
        trackFlagsEvent(OptimizelyEvents.VISIT_GYM_PAGE);
    }, [trackFlagsEvent]);

    const resetCookieForJoinJourney = () => {
        const shoppingBasketCookie = getShoppingBasketCookie();

        updateShoppingBasketCookie({
            ...getLoggerPaymentData({
                basketCookie: shoppingBasketCookie,
            }),
            branchId,
            branchName: gymName,
            // Avoids pending 9000 cases
            // Retained so that BE can re-use the previously generated memberID.
            memberId: shoppingBasketCookie?.memberId,
            transactionId: shoppingBasketCookie?.transactionId,
        });

        trackFlagsEvent(OptimizelyEvents.START_JOIN_JOURNEY);
    };

    const mappedProducts = formatProductsForUI(products);
    const fromPrice = getFromPriceForProducts(mappedProducts);

    // TODO This logic will be removed once production gym content has been changed
    const branchIdParameter =
        !!gym && gym.branchId !== '' ? gym.branchId : branchId;

    // TODO This logic will be removed once production gym content has been changed
    const gymNameParameter =
        !!gym && gym.gymName !== '' ? gym.gymName : gymName;

    const membershipContextualProperties = {
        products: mappedProducts,
        text: 'Our Memberships',
        description: '',
        ctaHref: '/join-now/your-membership/',
        ctaLabel: 'Join Now',
        onSelect: onSelectProduct,
        productsAvailableAddons,
        openingHours,
        isKickerOffersRibbonEnabled,
        offSale,
        branchId,
        offPeakTimes,
    };

    const showOffPeakTimes =
        products.some(({ description }) => description.includes('Off Peak')) &&
        offPeakTimes &&
        offPeakTimes.some(times =>
            Object.entries(times).some(([, value]) => value.length > 0),
        );

    const contextualProperties = {
        Generic: {
            branchId: branchIdParameter,
            gymName: gymNameParameter,
            page: CmsPage.GymPage,
        },
        Membership: membershipContextualProperties,
        GymLocation: {
            address,
            openingHours,
            latitude,
            longitude,
            offPeakTimes,
            showOffPeakTimes,
        },
        GymHeader: {
            trustpilotData,
            address,
            openingHours,
            latitude,
            longitude,
            fromPrice,
            gymRating,
            isGoogleRatingEnabled: GOOGLE_RATING_WIDGET_ENABLED,
            onCtaClick: resetCookieForJoinJourney,
        },
        AnchorLinks: {
            promoSnackBarOptions,
        },
    };

    const {
        flags: {
            [OptimizelyFeatureFlags.MBT_PRICE_DRAWER_OPTIMISATION]: {
                enabled: isMBTOptimizationEnabled,
                variationKey: isMBTOptimizationEnabledVariationKey,
            },
        },
    } = useFlagsContext();

    const isMBTOptimization =
        isMBTOptimizationEnabled &&
        isMBTOptimizationEnabledVariationKey !== 'control_mbt';

    const filteredContent = isMBTOptimization
        ? content.filter(
              contentItem =>
                  contentItem._meta.schema !==
                      'https://thegymgroup.com/libs/ui/src/lib/components/PromoTileLayout/PromoTileLayout.schema.json' &&
                  !(
                      contentItem._meta.schema ===
                          'https://thegymgroup.com/libs/ui/src/lib/components/Heading/Heading.schema.json' &&
                      contentItem._meta.name.includes('Gym Deals')
                  ),
          )
        : content;

    return (
        <>
            <Head>
                <script
                    id="local-business-script"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(localBusinessData),
                    }}
                />
            </Head>
            {filteredContent.map(contentItem => (
                <CmsContent
                    /* eslint-disable-next-line no-underscore-dangle */
                    key={uuidv4()}
                    schema={contentItem}
                    contextualProps={contextualProperties}
                />
            ))}
        </>
    );
}

const pageParameters: GsspWithLoggerParameters = {
    applicationIdentifier,
    meta: {
        journeyInfo: {
            journeyName: 'JoinJourney',
            journeyStep: 'GymPage',
        },
    },
};

type GymPagePropertiesSsrProperties = Pick<
    GymPageProperties,
    | 'branchId'
    | 'branchStatus'
    | 'gymName'
    | 'products'
    | 'openingHours'
    | 'address'
    | 'latitude'
    | 'longitude'
    | 'trustpilotData'
    | 'productsAvailableAddons'
    | 'offPeakTimes'
    | 'isKickerOffersRibbonEnabled'
    | 'gymRating'
    | 'offSale'
    | 'localBusinessData'
>;

export const getServerSideProps = gsspWithLogger<
    GymPagePropertiesSsrProperties,
    { branchId: string; placeId?: string }
>(async (context, injectorParameters) => {
    const { isMobileSession, loggerParameters, logger } = injectorParameters;
    const {
        publicRuntimeConfig: { APP_BASE_PATH },
        serverRuntimeConfig: {
            APP_INTERNAL_BASE_URL,
            TRUSTPILOT_API_KEY,
            GOOGLE_MAPS_API_SERVER_SIDE_KEY,
        },
    }: LocalConfig = getConfig();

    const domain = `${APP_INTERNAL_BASE_URL}${APP_BASE_PATH}`;
    const { params } = context;

    const branchId = params?.branchId as string;

    const gym = await axiosInstance.get<any, AxiosResponse<GymDto>>(
        `${domain}/api/proxy/gyms/${branchId}/`,
    );

    let products: GymProduct[] = [];

    if (!gym.data.offSale) {
        const optionsResponse = await axiosInstance.get<
            { BranchId: string },
            AxiosResponse<JoinOptionsResponse>
        >(`${domain}/api/proxy/join/options/gym/`, {
            params: {
                BranchId: branchId,
            },
        });
        products = optionsResponse.data.products;
    }

    const trustpilotData = await getTrustpilotData(TRUSTPILOT_API_KEY);
    const trustpilotDataProperty =
        trustpilotData !== null ? { trustpilotData } : {};

    let googleRating: PlaceRating | null = null;
    if (params?.placeId && GOOGLE_RATING_WIDGET_ENABLED) {
        googleRating = await getPlaceRating(
            params.placeId,
            GOOGLE_MAPS_API_SERVER_SIDE_KEY,
            logger,
            loggerParameters,
        );
    }

    const gymRating =
        googleRating && googleRating.rating >= MINIMUM_RATING_TO_SHOW
            ? googleRating
            : null;

    const productsAvailableAddons = await getProductsAvailableAddons(
        branchId,
        products,
    );

    // TODO: Remove this once kicker offers are enabled
    const isKickerOffersRibbonEnabled = false;

    interface MediaContentData {
        _meta: Metadata;
        tiles: Tile[];
    }

    const mediaGalleryContent = (
        params as { content?: MediaContentData[] }
    ).content?.find(
        contentItem =>
            contentItem._meta.schema ===
            'https://thegymgroup.com/libs/ui/src/lib/components/MediaGallery/MediaGallery.schema.json',
    );

    const photoUrls = mediaGalleryContent?.tiles.map(tile => {
        if (typeof tile.image.image === 'string') {
            return tile.image.image;
        }

        const { name, endpoint, defaultHost } = tile.image.image;
        return amplienceUrl(defaultHost, endpoint, name, {
            height: 545,
            width: 1440,
        });
    });

    const localBusinessData = getLocalBusinessData(
        gym.data,
        photoUrls ?? [],
        gymRating,
    );

    return {
        props: {
            branchId: gym.data.branchId,
            gymName: gym.data.name,
            products,
            address: gym.data.address,
            openingHours: gym.data.openingHours,
            latitude: gym.data.latitude,
            longitude: gym.data.longitude,
            productsAvailableAddons,
            offPeakTimes: gym.data.offPeakTimes,
            isKickerOffersRibbonEnabled,
            gymRating,
            offSale: gym.data.offSale,
            branchStatus: gym.data.status,
            localBusinessData,
            ...trustpilotDataProperty,
        },
    };
}, pageParameters);
